<template>
  <div class="session-card">
    <!-- Session header with time and status -->
    <div class="session-header">
      <div class="session-time-info">
        <div class="session-time">{{ formattedStartTime }}</div>
        <div v-if="formattedEndTime" class="session-duration">
          - {{ formattedEndTime }}
          <span v-if="getDurationText()" class="duration-text">({{ getDurationText() }})</span>
        </div>
      </div>
      
      <div class="session-status">
        <span v-if="isLive" class="status-badge live">
          <span class="live-indicator"></span>
          Live Now
        </span>
        <span v-else-if="isUpcomingSession" class="status-badge upcoming">
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12,6 12,12 16,14"></polyline>
          </svg>
          Upcoming
        </span>
        <span v-else-if="isPastSession" class="status-badge past">
          Completed
        </span>
      </div>
    </div>

    <!-- Session main content -->
    <div class="session-content">
      <h4 class="session-title" @click="handleSessionClick">{{ session.title }}</h4>
      
      <div v-if="session.description" class="session-description">
        <p>{{ session.description }}</p>
      </div>

      <!-- Location and capacity info -->
      <div class="session-meta">
        <div v-if="session.location" class="meta-item location">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
          {{ session.location }}
        </div>
        
        <div v-if="session.capacity" class="meta-item capacity">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
          {{ session.attendee_count || 0 }} / {{ session.capacity }}
        </div>

        <div v-if="session.session_type" class="meta-item session-type">
          <span class="type-badge" :class="getTypeClass(session.session_type)">
            {{ getTypeLabel(session.session_type) }}
          </span>
        </div>
      </div>

      <!-- Tags/Topics -->
      <div v-if="session.tags && session.tags.length > 0" class="session-tags">
        <span 
          v-for="tag in session.tags.slice(0, 3)" 
          :key="tag" 
          class="session-tag"
          @click="handleTagClick(tag)"
        >
          {{ tag }}
        </span>
        <span v-if="session.tags.length > 3" class="tag-more">
          +{{ session.tags.length - 3 }} more
        </span>
      </div>

      <!-- Speakers -->
      <div v-if="session.speakers && session.speakers.length > 0" class="session-speakers">
        <h5 class="speakers-title">Speakers:</h5>
        <div class="speakers-list">
          <div 
            v-for="speaker in session.speakers" 
            :key="speaker.id"
            class="speaker-item"
            @click="handleSpeakerClick(speaker)"
          >
            <div class="speaker-photo">
              <img 
                v-if="speaker.profile_image_url" 
                :src="speaker.profile_image_url" 
                :alt="`${speaker.name} profile photo`"
                @error="handleImageError"
              />
              <div v-else class="photo-placeholder">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
            </div>
            <div class="speaker-info">
              <div class="speaker-name">{{ speaker.name }}</div>
              <div v-if="speaker.title" class="speaker-title">{{ speaker.title }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Action buttons -->
    <div v-if="session.available_actions && session.available_actions.length > 0" class="session-actions">
      <button
        v-for="action in getDisplayedActions()"
        :key="action"
        @click="handleActionClick(action)"
        class="action-btn"
        :class="[`action-${action}`]"
      >
        <span class="action-icon">
          <svg v-if="action === 'add_to_schedule'" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="12" y1="11" x2="12" y2="17"></line>
            <line x1="9" y1="14" x2="15" y2="14"></line>
          </svg>
          <svg v-else-if="action === 'join_session'" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M15 3h6v6"></path>
            <path d="M10 14 21 3"></path>
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
          </svg>
          <svg v-else-if="action === 'view_details'" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M12 16v-4"></path>
            <path d="M12 8h.01"></path>
          </svg>
          <svg v-else-if="action === 'share_session'" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="18" cy="5" r="3"></circle>
            <circle cx="6" cy="12" r="3"></circle>
            <circle cx="18" cy="19" r="3"></circle>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
          </svg>
          <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 11H5a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h4"></path>
            <path d="M9 21V9a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v12"></path>
            <path d="M21 21h-8"></path>
            <path d="M17 8v3"></path>
          </svg>
        </span>
        {{ getActionLabel(action) }}
      </button>
    </div>

    <!-- Session match score (if from search) -->
    <div v-if="session.match_score !== undefined" class="match-score">
      Relevance: {{ Math.round(session.match_score * 100) }}%
    </div>
  </div>
</template>

<script>
import { formatTime, calculateDuration, isHappeningNow, isPast, isUpcoming } from '../../utils/datetime';

export default {
  name: 'SessionCard',
  props: {
    session: {
      type: Object,
      required: true
    },
    timezone: {
      type: String,
      default: 'UTC'
    },
    maxActions: {
      type: Number,
      default: 3
    }
  },
  emits: ['action-click', 'session-click', 'speaker-click', 'tag-click'],
  computed: {
    formattedStartTime() {
      if (this.session.start_datetime) {
        return formatTime(this.session.start_datetime, this.timezone);
      }
      return this.session.start_time || '';
    },
    
    formattedEndTime() {
      if (this.session.end_datetime) {
        return formatTime(this.session.end_datetime, this.timezone);
      }
      return this.session.end_time || '';
    },
    
    isLive() {
      if (this.session.start_datetime && this.session.end_datetime) {
        return isHappeningNow(this.session.start_datetime, this.session.end_datetime, this.timezone);
      }
      return this.session.is_live || false;
    },
    
    isUpcomingSession() {
      if (this.session.start_datetime) {
        return isUpcoming(this.session.start_datetime, this.timezone);
      }
      return this.session.is_upcoming || false;
    },
    
    isPastSession() {
      if (this.session.end_datetime) {
        return isPast(this.session.end_datetime, this.timezone);
      }
      return this.session.is_past || false;
    }
  },
  methods: {
    handleImageError(event) {
      event.target.style.display = 'none';
    },
    
    getDurationText() {
      if (this.session.start_datetime && this.session.end_datetime) {
        return calculateDuration(this.session.start_datetime, this.session.end_datetime, this.timezone);
      }
      
      // Fallback to simple calculation if datetime fields not available
      if (!this.session.start_time || !this.session.end_time) return '';
      
      const startParts = this.session.start_time.split(':');
      const endParts = this.session.end_time.split(':');
      
      if (startParts.length >= 2 && endParts.length >= 2) {
        const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
        const endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
        const durationMinutes = endMinutes - startMinutes;
        
        if (durationMinutes > 0) {
          const hours = Math.floor(durationMinutes / 60);
          const minutes = durationMinutes % 60;
          
          if (hours > 0) {
            return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
          } else {
            return `${minutes}m`;
          }
        }
      }
      return '';
    },
    
    getTypeClass(sessionType) {
      const typeClasses = {
        'keynote': 'type-keynote',
        'workshop': 'type-workshop',
        'panel': 'type-panel',
        'presentation': 'type-presentation',
        'networking': 'type-networking',
        'break': 'type-break'
      };
      return typeClasses[sessionType] || 'type-default';
    },
    
    getTypeLabel(sessionType) {
      const typeLabels = {
        'keynote': 'Keynote',
        'workshop': 'Workshop',
        'panel': 'Panel Discussion',
        'presentation': 'Presentation',
        'networking': 'Networking',
        'break': 'Break'
      };
      return typeLabels[sessionType] || sessionType;
    },
    
    getDisplayedActions() {
      if (!this.session.available_actions) return [];
      return this.session.available_actions.slice(0, this.maxActions);
    },
    
    getActionLabel(action) {
      const actionLabels = {
        'add_to_schedule': 'Add to My Schedule',
        'join_session': 'Join',
        'view_details': 'Details',
        'share_session': 'Share',
        'set_reminder': 'Remind Me'
      };
      return actionLabels[action] || action.replace('_', ' ');
    },
    
    handleActionClick(action) {
      this.$emit('action-click', {
        action,
        session: this.session
      });
    },
    
    handleSessionClick() {
      this.$emit('session-click', this.session);
    },
    
    handleSpeakerClick(speaker) {
      this.$emit('speaker-click', {
        speaker,
        session: this.session
      });
    },
    
    handleTagClick(tag) {
      this.$emit('tag-click', {
        tag,
        session: this.session
      });
    }
  }
};
</script>

<style scoped>
.session-card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 16px;
  background-color: #fff;
  transition: all 0.2s ease;
  margin-bottom: 12px;
}

.session-card:hover {
  border-color: #adb5bd;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.session-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.session-time-info {
  display: flex;
  flex-direction: column;
}

.session-time {
  font-size: 1.1rem;
  font-weight: 600;
  color: #0d6efd;
  line-height: 1.2;
}

.session-duration {
  font-size: 0.85rem;
  color: #6c757d;
  margin-top: 2px;
}

.duration-text {
  font-weight: 500;
  margin-left: 4px;
}

.session-status {
  flex-shrink: 0;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge.live {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.status-badge.upcoming {
  background-color: #cce8ff;
  color: #004085;
  border: 1px solid #b3d9ff;
}

.status-badge.past {
  background-color: #e2e3e5;
  color: #6c757d;
  border: 1px solid #d6d8db;
}

.live-indicator {
  width: 6px;
  height: 6px;
  background-color: #28a745;
  border-radius: 50%;
  margin-right: 4px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}

.session-content {
  margin-bottom: 12px;
}

.session-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #212529;
  line-height: 1.4;
  cursor: pointer;
  transition: color 0.2s ease;
}

.session-title:hover {
  color: #0d6efd;
}

.session-description {
  margin-bottom: 12px;
}

.session-description p {
  font-size: 0.9rem;
  color: #495057;
  line-height: 1.5;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.session-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}

.meta-item {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: #6c757d;
}

.meta-item svg {
  margin-right: 4px;
  opacity: 0.8;
}

.meta-item.location {
  color: #dc3545;
}

.meta-item.capacity {
  color: #6f42c1;
}

.type-badge {
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.type-keynote {
  background-color: #fff3cd;
  color: #856404;
}

.type-workshop {
  background-color: #d1ecf1;
  color: #0c5460;
}

.type-panel {
  background-color: #e7d6ff;
  color: #6f42c1;
}

.type-presentation {
  background-color: #cfe2ff;
  color: #084298;
}

.type-networking {
  background-color: #d1e7dd;
  color: #0f5132;
}

.type-break {
  background-color: #f8d7da;
  color: #721c24;
}

.type-default {
  background-color: #e9ecef;
  color: #495057;
}

.session-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 12px;
}

.session-tag {
  background-color: #f8f9fa;
  color: #495057;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #dee2e6;
}

.session-tag:hover {
  background-color: #e9ecef;
  color: #212529;
}

.tag-more {
  color: #6c757d;
  font-size: 0.7rem;
  font-style: italic;
}

.session-speakers {
  margin-bottom: 12px;
  padding: 8px 0;
  border-top: 1px solid #f1f3f4;
}

.speakers-title {
  font-size: 0.8rem;
  font-weight: 600;
  color: #495057;
  margin: 0 0 8px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.speakers-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.speaker-item {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  background-color: #f8f9fa;
  border-radius: 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border: 1px solid #e9ecef;
}

.speaker-item:hover {
  background-color: #e9ecef;
}

.speaker-photo {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 6px;
  flex-shrink: 0;
  background-color: #fff;
  border: 1px solid #dee2e6;
}

.speaker-photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #adb5bd;
}

.speaker-info {
  min-width: 0;
}

.speaker-name {
  font-size: 0.8rem;
  font-weight: 500;
  color: #212529;
  white-space: nowrap;
}

.speaker-title {
  font-size: 0.7rem;
  color: #6c757d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.session-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f1f3f4;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background-color: #fff;
  color: #495057;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
}

.action-btn:hover {
  border-color: #adb5bd;
  background-color: #f8f9fa;
  transform: translateY(-1px);
}

.action-icon {
  margin-right: 4px;
  display: flex;
  align-items: center;
}

.action-add_to_schedule {
  color: #198754;
  border-color: #b8dfcb;
}

.action-add_to_schedule:hover {
  background-color: #d1e7dd;
  border-color: #a3cfbb;
}

.action-join_session {
  color: #0d6efd;
  border-color: #b6d4fe;
}

.action-join_session:hover {
  background-color: #cfe2ff;
  border-color: #9ec5fe;
}

.action-view_details {
  color: #6c757d;
  border-color: #ced4da;
}

.action-view_details:hover {
  background-color: #e9ecef;
  border-color: #adb5bd;
}

.match-score {
  font-size: 0.7rem;
  color: #6c757d;
  text-align: right;
  margin-top: 8px;
  font-weight: 500;
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .session-card {
    padding: 12px;
  }
  
  .session-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .session-meta {
    flex-direction: column;
    gap: 6px;
  }
  
  .speakers-list {
    flex-direction: column;
  }
  
  .speaker-item {
    width: 100%;
  }
  
  .action-btn {
    font-size: 0.7rem;
    padding: 3px 6px;
  }
}
</style>